import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { contractApi } from '../../api/contracts';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { IconActionButton } from '../../components/IconActionButton';
import { Eye } from 'lucide-react';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', hr_review: 'HR Review', returned: 'Returned for Redraft', approved: 'Approved',
  signed: 'Signed', finalised: 'Finalised', expired: 'Expired', terminated: 'Terminated', rejected: 'Rejected',
};

/**
 * Contract Workspace architecture - org-wide list, the entry point
 * every contract is now reachable from directly, not just buried
 * inside each employee's own page.
 */
export function ContractsPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const contractsQuery = useQuery({ queryKey: ['contracts-all', statusFilter], queryFn: () => contractApi.listAll(statusFilter ? { status: statusFilter } : undefined) });

  return (
    <div className="page">
      <PageHeader title="Contracts" description="Every staff contract across the organization - to create a new one, start from the employee's own profile." />

      <div className="panel">
        <SectionHeader
          title="All Contracts"
          actions={
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          }
        />
        <div className="panel-body">
          {contractsQuery.data && (
            <DataTable
              rows={contractsQuery.data.data}
              rowKey={(c) => c.id}
              emptyMessage="No contracts yet - create one from the employee's own profile page."
              columns={[
                { header: 'Reference', render: (c) => c.contract_reference },
                { header: 'Employee', render: (c) => <Link to={`/hr/contracts/${c.id}`}>{c.employee_name ?? '-'}</Link> },
                { header: 'Employment Type', render: (c) => c.employment_type ?? '-' },
                { header: 'Start Date', render: (c) => c.start_date },
                { header: 'End Date', render: (c) => c.end_date ?? 'Open-ended' },
                { header: 'Status', render: (c) => <StatusBadge status={c.status} label={STATUS_LABELS[c.status]} /> },
                { header: '', render: (c) => <IconActionButton icon={Eye} label="View" onClick={() => navigate(`/hr/contracts/${c.id}`)} /> },
              ]}
            />
          )}
        </div>
      </div>
    </div>
  );
}
